import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, RotateCcw, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "./ui/sonner";
import { useApp } from "../context/AppContext";
import { T } from "../lib/i18n";
import { fmtDT } from "../lib/date";

const ALLOW = ["admin", "eng_manager", "ceo"];

export const RecycleSurgeBanner = () => {
  const { api, user } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/projects/recycle/surge").then(({ data }) => setD(data)).catch(() => setD(null)), [api]);
  useEffect(() => {
    if (!ALLOW.includes(user?.role)) return undefined;
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load, user?.role]);
  const active = (d?.surges || []).filter((s) => !s.dismissed);
  if (!active.length) return null;
  const restore = async (s) => {
    if (!window.confirm(T("一键恢复这 {0} 个项目到原状态？", [s.count]))) return;
    setBusy(s.key);
    try { const { data } = await api.post("/projects/recycle/surge/restore", { key: s.key }); toast.success(T("已恢复 {0} 个项目，当前活跃项目 {1} 个", [data.restored, data.active])); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const dismiss = async (s) => {
    if (!window.confirm(T("确认这 {0} 个项目是有意删除，不再提示？（项目仍留在回收站，可随时恢复）", [s.count]))) return;
    try { await api.post("/projects/recycle/surge/dismiss", { key: s.key }); load(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-1 mb-4 -mt-1" data-testid="recycle-surge-banner">
      {active.map((s) => (
        <div key={s.key} className="bg-red-600 text-white px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm shadow-md" data-testid={`recycle-surge-${s.key}`}>
          <AlertOctagon className="h-5 w-5 shrink-0 animate-pulse" />
          <span className="font-semibold">{T("回收站异常")}：</span>
          <span data-testid="recycle-surge-text">{T("{0} 个项目在 {1} 起 1 小时内被移入回收站", [s.count, fmtDT(s.since)])} · {T("操作者")} {s.actors.join("、")} · {T("合同额")} ${Math.round(s.contractTotal || 0).toLocaleString()}</span>
          <span className="text-red-100 text-xs truncate max-w-[360px]" title={s.sample.join(" / ")}>{s.sample.slice(0, 3).join(" / ")}{s.count > 3 ? " …" : ""}</span>
          <span className="ml-auto flex items-center gap-1.5">
            <button type="button" onClick={() => restore(s)} disabled={busy === s.key} data-testid="recycle-surge-restore" className="inline-flex items-center gap-1 bg-white text-red-700 font-bold text-xs px-3 py-1.5 rounded-full hover:bg-red-50 disabled:opacity-60">{busy === s.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}{T("一键全部恢复")}</button>
            <button type="button" onClick={() => nav("/recycle-bin")} data-testid="recycle-surge-open" className="inline-flex items-center gap-1 border border-white/60 text-xs px-3 py-1.5 rounded-full hover:bg-white/10"><Trash2 className="h-3.5 w-3.5" />{T("查看回收站")}</button>
            <button type="button" onClick={() => dismiss(s)} data-testid="recycle-surge-dismiss" className="p-1 rounded-full hover:bg-white/10" title={T("有意删除，不再提示")}><X className="h-4 w-4" /></button>
          </span>
        </div>
      ))}
    </div>
  );
};
